import { useState, useEffect } from 'react';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import { makeStyles } from '@material-ui/core';
import { darken, lighten, alpha } from '@material-ui/core/styles/colorManipulator';
import ListIcon from '@material-ui/icons/List';
import EqualizerIcon from '@material-ui/icons/Equalizer';
import { Trans, t } from '@lingui/macro';
import { omit } from 'lodash';

import Panel from '../../page-framework/panel/PanelDecorator';
import {
    PANEL_CATEGORIES,
    PANEL_PRIORITIES,
    STATUS_LIST_PANEL_PREVIEW_IMAGE,
} from '../../page-framework/panel/PanelRegistry';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import { useId, useOptions } from '../../page-framework/panel/Panel';
import { useDashboardContext } from '../../page-framework';
import SiteTests from '../../service-delivery/SiteTests';
import PerformanceList from '../../service-delivery/PerformanceList';
import useIsPrinting from '../../../utilities/PrintUtilities';
import { usePageStateValue } from '../../../stores/PageStateStore';
import {
    siteFilterId,
    businessServiceFilterId,
    statusFilterId,
    testTypeFilterId,
    timeRangeFilterId,
} from '../../filters';

const STATUS_TITLE = t`View the status trend for service tests being run at this site`;
const PERFORMANCE_TITLE = t`View performance charts for service tests being run at this site`;

const TAB_STATUS = 0;
const TAB_PERFORMANCE = 1;
const DEFAULT_TAB = TAB_STATUS;

const title = t`Site Service Tests`;

const performanceDataSource = {
    url: '/query/table',
    params: {
        type: 'agent,test',
        entityFields: 'name,tags',
        metrics: 'status,availPercent,*primary*',
        rowLimit: 10,
        rowStart: 0,
        sort: '-status',
        trends: 'status,availability,*,*thresholds*',
    },
};

const useStyles = makeStyles((theme) => ({
    tab: {
        '& .MuiTab-wrapper': {
            flexDirection: 'row',

            '& svg': {
                marginRight: theme.spacing(1),
            },
        },
    },
    tabs: {
        borderBottom: `1px solid ${
            theme.palette.type === 'light'
                ? lighten(alpha(theme.palette.divider, 1), 0.85)
                : darken(alpha(theme.palette.divider, 1), 0.85)
        }`,
        marginBottom: theme.spacing(1),

        '& .MuiTab-labelIcon': {
            minHeight: '50px',

            '& .MuiTab-wrapper > *:first-child': {
                marginBottom: 0,
            },
        },
    },
}));

function SiteTestsPanel() {
    const classes = useStyles();
    const isPrinting = useIsPrinting();
    const id = useId();
    const { editing, saving } = useDashboardContext();
    const [options, setOptions] = useOptions();
    const tabKey = `${id}-Tab`;
    const [selectedTab, pageStateActions] = usePageStateValue({
        key: tabKey,
        defaultValue: options?.selectedTab || DEFAULT_TAB,
    });
    const [workingOptions, setWorkingOptions] = useState({
        selectedTab,
    });

    const handleTabChange = (tab) => {
        if (tab === null) return;

        const isDefaultTab = tab === DEFAULT_TAB;
        const newOptions = isDefaultTab
            ? omit(options || {}, 'selectedTab')
            : {
                  ...(options || {}),
                  selectedTab: tab,
              };

        if (editing) {
            setOptions(newOptions);
        } else {
            setWorkingOptions(newOptions);

            // If the selected tab is the same as the options value, or if the selected tab is the default tab
            // and no options value is present, we want to clear the page state to keep the URL clean.  The
            // URL value is only necessary if the working tab is different from the default or the options value.
            const clearPageState = tab === options?.selectedTab || (isDefaultTab && !options?.selectedTab);

            if (clearPageState) {
                pageStateActions.clearValue(tabKey);
            } else {
                pageStateActions.setValue(tabKey, tab);
            }
        }
    };

    useEffect(() => {
        // Replace the working options with the edited options when saving so the restored
        // working state reflects the saved state.
        if (saving) setWorkingOptions(options);
    }, [saving, options]);

    const tab = (editing ? options?.selectedTab : workingOptions.selectedTab) || DEFAULT_TAB;

    return (
        <>
            {!isPrinting && (
                <Tabs
                    className={classes.tabs}
                    value={tab}
                    onChange={(e, index) => handleTabChange(index)}
                    aria-label="agent detail tabs"
                >
                    <Tab
                        className={classes.tab}
                        id="agent-detail-tab-status"
                        icon={<ListIcon />}
                        label={<Trans>Status</Trans>}
                        title={STATUS_TITLE}
                    />
                    <Tab
                        className={classes.tab}
                        id="agent-detail-tab-performance"
                        icon={<EqualizerIcon />}
                        label={<Trans>Performance</Trans>}
                        title={PERFORMANCE_TITLE}
                    />
                </Tabs>
            )}
            {tab === TAB_STATUS && <SiteTests />}
            {tab === TAB_PERFORMANCE && <PerformanceList dataSource={performanceDataSource} />}
        </>
    );
}

SiteTestsPanel.propTypes = panelPropTypes;

SiteTestsPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    minContentHeight: 125,
    contentHeight: 375,
    autoHeight: true,
};

export default Panel({
    catalogEntry: {
        category: PANEL_CATEGORIES.SITE,
        priority: PANEL_PRIORITIES.SUBLIST,
        title,
        description: t`Service tests status and performance for a selected site`,
        previewImage: STATUS_LIST_PANEL_PREVIEW_IMAGE,
    },
    filters: {
        required: [timeRangeFilterId, siteFilterId],
        optional: [businessServiceFilterId, statusFilterId, testTypeFilterId],
    },
})(SiteTestsPanel);
